import { useRef, useState } from "react";
import { trackEvent } from "@/utils/mixpanel";
import mixpanel from "@/utils/mixpanel";
import { pushDataLayerEvent } from "@/utils/googleAnalytics";
import { getAttributionParams } from "@/lib/attribution";
import MacLinkEmailButton from "@/components/desktop/MacLinkEmailButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// "Text me the Mac link" — the iPhone answer to the download CTA.
//
// The visitor texts US: the tap opens Messages pre-filled with a request and a
// short ref code, addressed to Vovy's iMessage handle (an Apple ID on the
// studio Mac mini, served by BlueBubbles). The `mac-link-imessage` edge fn
// replies with the personalized download link. Inbound-first means no cold
// outbound — the thread lands in their main inbox, the link is tappable, and
// our Name & Photo shows.
//
// The ref code ties the text back to this web session: the intent (code +
// distinct_id + attribution) is posted with keepalive in the same tick as the
// sms: navigation, so iOS leaving Safari for Messages can't drop it and no
// await sits between the tap and the app switch.
//
// WHY AN <a href="sms:…">, NOT window.location: iOS browsers only reliably hand
// a custom scheme to another app when the user taps a real link. Chrome on iOS
// silently ignored the scripted `location.href = "sms:…"` (Oct 23: ten taps,
// ten intents logged, Messages never opened). The code is minted per render so
// the href is ready before the tap; onClick logs and lets the link navigate.
//
// The CTA opens a modal first (Oct 23): three numbered steps and a preview of
// the exact text, so "you're about to text us" is clear before Messages opens.
// The modal's "Open Messages" button is that real sms: link, and the intent is
// logged on that tap (once per code), so funnel taps still mean "went to
// Messages". Under it, "Or email it to me" opens the email flow for anyone who
// would rather not text, or whose in-app browser blocks sms: links.
//
// Hidden unless VITE_IMESSAGE_HANDLE is set — DownloadCta falls back to email.

export const IMESSAGE_HANDLE: string | undefined =
  (import.meta.env.VITE_IMESSAGE_HANDLE as string | undefined)?.trim() || undefined;

const INTENT_URL =
  "https://swrrttvhfnsiesmkbwia.supabase.co/functions/v1/mac-link-imessage";

/** No 0/O/1/I — the code is read and typed by humans. Mirrors CODE_RE server-side. */
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function makeCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join("");
}

interface Props {
  /** Which surface the request came from ("hero", "pricing", "installing"). */
  placement: string;
  className?: string;
  iconClassName?: string;
  label?: string;
  style?: React.CSSProperties;
}

export default function MacLinkTextButton({
  placement,
  className = "btn-gamey inline-flex items-center gap-2.5 bg-white hover:bg-slate-50 text-[#0B6BB0] font-bold uppercase text-sm xl:text-base tracking-wide px-8 py-4 xl:px-9 xl:py-[1.15rem] rounded-2xl transition-colors",
  iconClassName = "w-6 h-6 xl:w-7 xl:h-7",
  label = "Text me the Mac link",
  style,
}: Props) {
  const [open, setOpen] = useState(false);
  // A fresh code each time the modal opens; it stays put while it's open so the
  // preview matches what they send.
  const [code, setCode] = useState(makeCode);
  const loggedCode = useRef<string | null>(null);
  if (!IMESSAGE_HANDLE) return null;

  const text = `Send me the Vovy Mac link ${code}`;
  const href = `sms:${IMESSAGE_HANDLE}&body=${encodeURIComponent(text)}`;

  const logIntent = () => {
    if (loggedCode.current === code) return; // one intent per code, however many taps
    loggedCode.current = code;
    let distinctId: string | undefined;
    try {
      const id = mixpanel.get_distinct_id();
      if (typeof id === "string" && id.length >= 8) distinctId = id;
    } catch {
      /* identity optional */
    }
    try {
      void fetch(INTENT_URL, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "intent",
          code,
          placement,
          distinct_id: distinctId,
          attribution: getAttributionParams(),
        }),
      }).catch(() => {});
    } catch {
      /* the text still works without the intent — just unattributed */
    }
    try {
      trackEvent("mac_link_text_tapped", { placement });
      // Same GTM event as the email variant — both are "wants the Mac link".
      pushDataLayerEvent("mac_link_requested", { source: "mac_link", placement, channel: "imessage" });
    } catch {
      /* analytics must never break the CTA */
    }
  };

  const steps = [
    { title: `Send this text to ${IMESSAGE_HANDLE}`, detail: null },
    { title: "Open the link we text back", detail: "It arrives in a few seconds. Open it on your Mac: this chat is in Messages there too." },
    { title: "Download Vovy on your Mac", detail: "The link starts the download. Open the app and you're in." },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          setCode(makeCode());
          try {
            trackEvent("mac_link_text_modal_opened", { placement });
          } catch {
            /* analytics must never break the CTA */
          }
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          style={{
            ["--gamey-shadow" as string]: "#bcdcf2",
            ["--gamey-shadow-hover" as string]: "#9cc8ea",
            ...style,
          }}
          className={className}
        >
          {/* The real macOS Messages app icon (exported from Messages.app) — the
              flow ends in Messages, so the button shows where it goes. */}
          <img src="/desktop/apple-messages.png" alt="" aria-hidden="true" className={iconClassName} />
          {label}
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>Get Vovy on your Mac</DialogTitle>
          <DialogDescription>Vovy is a Mac app. Text us and we'll text you back the download link.</DialogDescription>
        </DialogHeader>

        <ol className="mt-1 space-y-4">
          {steps.map((s, i) => (
            <li key={s.title} className="flex gap-3">
              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#00A3FF] text-sm font-bold text-white">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-foreground">{s.title}</p>
                {s.detail && <p className="mt-0.5 text-sm text-muted-foreground">{s.detail}</p>}
                {i === 0 && (
                  <div className="mt-2 flex items-end justify-end gap-2">
                    {/* Preview of the pre-filled message, styled as a sent iMessage bubble. */}
                    <p className="max-w-[85%] rounded-[20px] rounded-br-md bg-[#0A84FF] px-3.5 py-2 text-[15px] leading-snug text-white">{text}</p>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-2 space-y-2">
          <a
            href={href}
            onClick={logIntent}
            style={{
              ["--gamey-shadow" as string]: "#0369a1",
              ["--gamey-shadow-hover" as string]: "#075985",
            }}
            className="btn-gamey w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#00A3FF] px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#00A3FF]/90"
          >
            <img src="/desktop/apple-messages.png" alt="" aria-hidden="true" className="h-5 w-5" />
            Open Messages
          </a>
          {/* Opens the email dialog on top of this one; same funnel, email channel. */}
          <MacLinkEmailButton
            placement={placement}
            label="Or email it to me"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-transparent py-1 text-xs text-muted-foreground underline underline-offset-2"
            iconClassName="h-3.5 w-3.5"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
